/**
 * Admin — "Duelo de Equipos".
 * Tabs: Nuevo (armar duelo) · En vivo (control anfitrión con QR) · Historial.
 * Reutiliza tiendas, vendedores, banco de preguntas y permisos existentes.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { jsPDF } from "jspdf";
import * as XLSX from "xlsx";
import {
  Swords, Users, Play, Pause, RotateCcw, StopCircle, Copy, Tv, Loader2,
  Trophy, FileDown, FileSpreadsheet, Repeat, History, Plus, Store as StoreIcon,
  UserPlus, X, RefreshCw, Edit3,
} from "lucide-react";

import { api } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { wsUrl, TEAM_COLOR, PlayerCard } from "@/components/duel/DuelBits";

const originOf = () => (typeof window === "undefined" ? "" : window.location.origin);
const copyText = async (t) => { try { await navigator.clipboard.writeText(t); return true; } catch { return false; } };

/* ----------------- Live host control ----------------- */
function HostPanel({ created, lang, onReplace, onEdit }) {
  const wsRef = useRef(null);
  const [state, setState] = useState(null);
  const [conn, setConn] = useState("connecting");
  const [dupBusy, setDupBusy] = useState(false);
  const [editBusy, setEditBusy] = useState(false);
  const playerUrl = `${originOf()}${created.player_path}`;
  const tvUrl = `${originOf()}${created.tv_path}`;

  useEffect(() => {
    let ws, alive = true;
    try { ws = new WebSocket(wsUrl(created.code)); } catch { setConn("error"); return; }
    wsRef.current = ws;
    ws.onopen = () => { if (!alive) return; setConn("open"); ws.send(JSON.stringify({ type: "join", role: "host", host_token: created.host_token })); };
    ws.onclose = () => alive && setConn("closed");
    ws.onmessage = (ev) => { let m; try { m = JSON.parse(ev.data); } catch { return; } if (m.status) setState(m); };
    return () => { alive = false; try { ws && ws.close(); } catch {} };
  }, [created.code, created.host_token]);

  const send = (m) => { const ws = wsRef.current; if (ws && ws.readyState === 1) ws.send(JSON.stringify(m)); };
  const status = state?.status || "lobby";

  const duplicate = async () => {
    setDupBusy(true);
    try {
      const { data } = await api.post(`/team-duel/matches/${created.match_id}/rematch`, {});
      if (data.ok) { toast.success(lang === "es" ? "Juego duplicado — nueva sala lista" : "Game duplicated — new room ready"); onReplace?.(data); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setDupBusy(false); }
  };
  const resetGame = () => {
    if (!window.confirm(lang === "es"
      ? "¿Reiniciar el juego? Se borran puntos y errores (mismo código y QR)."
      : "Reset the game? Scores and strikes are cleared (same code and QR).")) return;
    send({ type: "reset" });
    toast.success(lang === "es" ? "Juego reiniciado" : "Game reset");
  };
  const editSetup = async () => {
    setEditBusy(true);
    try {
      const { data } = await api.get(`/team-duel/matches/${created.match_id}`);
      onEdit?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setEditBusy(false); }
  };

  const Btn = ({ onClick, disabled, color, icon: Icon, label, testid }) => (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
      className={`brutal-btn ${color} text-white px-4 py-3 font-display font-black inline-flex items-center gap-2 disabled:opacity-40`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  return (
    <div className="space-y-4" data-testid="duel-host-panel">
      <div className="brutal bg-white p-4">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div>
            <div className="text-[10px] font-display font-black uppercase tracking-widest text-canales-red">{lang === "es" ? "Sala creada" : "Room created"}</div>
            <div className="font-display font-black text-4xl tracking-widest">{created.code}</div>
            <div className="text-xs text-neutral-500 mt-1">{conn === "open" ? (lang === "es" ? "Conectado" : "Connected") : conn}</div>
          </div>
          <div className="text-center">
            <QRCodeCanvas value={playerUrl} size={120} includeMargin data-testid="duel-qr" />
            <div className="text-[10px] font-display font-black uppercase tracking-widest mt-1">{lang === "es" ? "QR jugadores" : "Players QR"}</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <button onClick={async () => (await copyText(playerUrl)) && toast.success(lang === "es" ? "Enlace de jugadores copiado" : "Player link copied")} className="brutal-sm bg-canales-blue text-white px-3 py-2 text-xs font-display font-bold inline-flex items-center gap-1" data-testid="duel-copy-player"><Copy className="w-3 h-3" /> {lang === "es" ? "Enlace jugadores" : "Player link"}</button>
          <button onClick={async () => (await copyText(tvUrl)) && toast.success(lang === "es" ? "Enlace TV copiado" : "TV link copied")} className="brutal-sm bg-white px-3 py-2 text-xs font-display font-bold inline-flex items-center gap-1" data-testid="duel-copy-tv"><Copy className="w-3 h-3" /> {lang === "es" ? "Enlace TV" : "TV link"}</button>
          <a href={tvUrl} target="_blank" rel="noreferrer" className="brutal-sm bg-canales-yellow text-black px-3 py-2 text-xs font-display font-black inline-flex items-center gap-1" data-testid="duel-open-tv"><Tv className="w-3 h-3" /> {lang === "es" ? "Abrir tablero TV" : "Open TV board"}</a>
        </div>
      </div>

      {/* Controls */}
      <div className="brutal bg-white p-4 flex flex-wrap gap-2 items-center" data-testid="duel-controls">
        <Btn onClick={() => send({ type: "start" })} disabled={status !== "lobby"} color="bg-canales-red" icon={Play} label={lang === "es" ? "Iniciar" : "Start"} testid="duel-start" />
        <Btn onClick={() => send({ type: "pause" })} disabled={!["playing", "steal", "sudden_death"].includes(status)} color="bg-canales-blue" icon={Pause} label={lang === "es" ? "Pausar" : "Pause"} testid="duel-pause" />
        <Btn onClick={() => send({ type: "resume" })} disabled={status !== "paused"} color="bg-green-600" icon={RotateCcw} label={lang === "es" ? "Reanudar" : "Resume"} testid="duel-resume" />
        <Btn onClick={() => { if (window.confirm(lang === "es" ? "¿Finalizar el duelo?" : "Finish the duel?")) send({ type: "finish" }); }} disabled={status === "done" || status === "lobby"} color="bg-neutral-800" icon={StopCircle} label={lang === "es" ? "Finalizar" : "Finish"} testid="duel-finish" />
        <div className="ml-auto brutal-sm bg-canales-cream px-3 py-1 font-display font-black text-sm uppercase tracking-widest">{status}</div>
      </div>

      {/* Room actions: duplicate / reset / edit */}
      <div className="brutal bg-white p-4 flex flex-wrap gap-2 items-center" data-testid="duel-room-actions">
        <div className="text-[10px] font-display font-black uppercase tracking-widest text-neutral-500 mr-1">{lang === "es" ? "Sala" : "Room"}</div>
        <button onClick={duplicate} disabled={dupBusy} data-testid="duel-duplicate" className="brutal-btn bg-canales-yellow text-black px-4 py-3 font-display font-black inline-flex items-center gap-2 disabled:opacity-40">
          {dupBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Repeat className="w-4 h-4" />} {lang === "es" ? "Duplicar" : "Duplicate"}
        </button>
        <button onClick={resetGame} disabled={status === "lobby"} data-testid="duel-reset" className="brutal-btn bg-canales-blue text-white px-4 py-3 font-display font-black inline-flex items-center gap-2 disabled:opacity-40">
          <RefreshCw className="w-4 h-4" /> {lang === "es" ? "Reiniciar" : "Reset"}
        </button>
        <button onClick={editSetup} disabled={editBusy} data-testid="duel-edit" className="brutal-btn bg-white text-black px-4 py-3 font-display font-black inline-flex items-center gap-2 disabled:opacity-40">
          {editBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Edit3 className="w-4 h-4" />} {lang === "es" ? "Editar" : "Edit"}
        </button>
      </div>

      {/* Mini live scoreboard */}
      {state && (
        <div className="grid grid-cols-2 gap-3">
          {["A", "B"].map((t) => (
            <div key={t} className="brutal bg-white p-3">
              <div className="flex items-center justify-between">
                <span className="font-display font-black truncate" style={{ color: TEAM_COLOR[t] }}>{state.team_names?.[t]}</span>
                <span className="font-display font-black text-2xl text-canales-red">{state.team_scores?.[t] ?? 0}</span>
              </div>
              <div className="grid grid-cols-3 gap-1 mt-2">
                {(state.teams?.[t]?.players || []).map((p) => (
                  <PlayerCard key={p.seller_id} player={p} isAnswerer={state.answerer === p.seller_id} maxStrikes={state.max_strikes} lang={lang} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ----------------- Setup ----------------- */
function SetupTab({ lang, onCreated, initial }) {
  const [stores, setStores] = useState([]);
  const [storeLocked, setStoreLocked] = useState(false);
  const [store, setStore] = useState("");
  const [roster, setRoster] = useState([]);
  const [assign, setAssign] = useState({}); // seller_id -> 'A' | 'B'
  const [manual, setManual] = useState([]); // [{id, name, team}]
  const [manualName, setManualName] = useState("");
  const [teamA, setTeamA] = useState("Equipo A");
  const [teamB, setTeamB] = useState("Equipo B");
  const [questions, setQuestions] = useState([]);
  const [cat, setCat] = useState("");
  const [selQ, setSelQ] = useState([]);
  const [cfg, setCfg] = useState({ points_per_correct: 10, seconds_per_question: 30, max_strikes: 3 });
  const [busy, setBusy] = useState(false);
  const pendingAssignRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/team-duel/stores");
        setStores(data.stores || []);
        setStoreLocked(!!data.locked);
        if (data.locked && data.stores?.[0]) setStore(data.stores[0]);
      } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
      try { const { data } = await api.get("/team-duel/questions"); setQuestions(data.questions || []); } catch {}
    })();
  }, []);

  useEffect(() => {
    if (!store && !storeLocked) { setRoster([]); return; }
    (async () => {
      try {
        const { data } = await api.get(`/team-duel/roster${store ? `?store=${encodeURIComponent(store)}` : ""}`);
        const sellers = data.sellers || [];
        setRoster(sellers);
        const valid = new Set(sellers.map((s) => s.seller_id));
        const pend = pendingAssignRef.current;
        if (pend) {
          const next = {};
          for (const [sid, t] of Object.entries(pend)) if (valid.has(sid)) next[sid] = t;
          setAssign(next);
          pendingAssignRef.current = null;
        } else {
          setAssign((prev) => {
            const next = {};
            for (const [sid, t] of Object.entries(prev)) if (valid.has(sid)) next[sid] = t;
            return next;
          });
        }
      } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    })();
  }, [store, storeLocked]);

  // Prefill the whole form when editing an existing duel.
  useEffect(() => {
    if (!initial) return;
    setStore(initial.store || "");
    setTeamA(initial.team_a?.name || "Equipo A");
    setTeamB(initial.team_b?.name || "Equipo B");
    setCfg({
      points_per_correct: initial.config?.points_per_correct ?? 10,
      seconds_per_question: initial.config?.seconds_per_question ?? 30,
      max_strikes: initial.config?.max_strikes ?? 3,
    });
    setSelQ(initial.question_ids || []);
    const man = [];
    const pend = {};
    for (const [team, key] of [["A", "team_a"], ["B", "team_b"]]) {
      for (const p of (initial[key]?.players || [])) {
        if (String(p.seller_id || "").startsWith("m-")) man.push({ id: p.seller_id, name: p.name, team });
        else if (p.seller_id) pend[p.seller_id] = team;
      }
    }
    setManual(man);
    pendingAssignRef.current = pend;
    setAssign(pend);
  }, [initial]);

  const counts = useMemo(() => {
    const a = Object.values(assign).filter((v) => v === "A").length + manual.filter((m) => m.team === "A").length;
    const b = Object.values(assign).filter((v) => v === "B").length + manual.filter((m) => m.team === "B").length;
    return { a, b };
  }, [assign, manual]);

  const setTeam = (sid, team) => {
    setAssign((prev) => {
      const cur = prev[sid];
      const next = { ...prev };
      if (cur === team) { delete next[sid]; return next; }
      const rosterInTeam = Object.entries(next).filter(([k, v]) => v === team && k !== sid).length;
      const manualInTeam = manual.filter((m) => m.team === team).length;
      if (rosterInTeam + manualInTeam >= 3) { toast.error(lang === "es" ? `El ${team === "A" ? "Equipo A" : "Equipo B"} ya tiene 3` : "Team already has 3"); return prev; }
      next[sid] = team; return next;
    });
  };

  const addManual = (team) => {
    const name = manualName.trim();
    if (!name) { toast.error(lang === "es" ? "Escribe un nombre" : "Type a name"); return; }
    const inTeam = team === "A" ? counts.a : counts.b;
    if (inTeam >= 3) { toast.error(lang === "es" ? `El Equipo ${team} ya tiene 3` : "Team already has 3"); return; }
    setManual((m) => [...m, { id: `m-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, name, team }]);
    setManualName("");
  };
  const removeManual = (id) => setManual((m) => m.filter((x) => x.id !== id));

  const cats = useMemo(() => Array.from(new Set(questions.map((q) => q.category))).sort(), [questions]);
  const shownQ = cat ? questions.filter((q) => q.category === cat) : questions;
  const toggleQ = (qid) => setSelQ((s) => (s.includes(qid) ? s.filter((x) => x !== qid) : [...s, qid]));

  const create = async () => {
    const rosterFor = (team) => roster.filter((r) => assign[r.seller_id] === team).map((r) => ({ seller_id: r.seller_id }));
    const manualFor = (team) => manual.filter((m) => m.team === team).map((m) => ({ name: m.name }));
    const A = [...rosterFor("A"), ...manualFor("A")];
    const B = [...rosterFor("B"), ...manualFor("B")];
    if (A.length !== 3 || B.length !== 3) { toast.error(lang === "es" ? "Cada equipo necesita 3 jugadores" : "Each team needs 3 players"); return; }
    if (selQ.length < 1) { toast.error(lang === "es" ? "Selecciona al menos 1 pregunta" : "Select at least 1 question"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/team-duel/create", {
        store: storeLocked ? "" : store,
        team_a: { name: teamA.trim() || "Equipo A", players: A },
        team_b: { name: teamB.trim() || "Equipo B", players: B },
        question_ids: selQ,
        ...cfg,
      });
      if (!data.ok) throw new Error(data.detail || "Error");
      toast.success(lang === "es" ? "¡Duelo creado!" : "Duel created!");
      onCreated(data);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      {/* Store + config */}
      <div className="brutal bg-white p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <label className="block md:col-span-2">
          <span className="text-[10px] font-display font-black uppercase tracking-widest text-canales-blue">{lang === "es" ? "Tienda" : "Store"}</span>
          <select disabled={storeLocked} value={store} onChange={(e) => { setStore(e.target.value); setAssign({}); }} data-testid="duel-store" className="brutal-sm mt-1 w-full py-2 px-2 bg-white font-display font-bold disabled:opacity-70">
            <option value="">{lang === "es" ? "— Elige una tienda —" : "— Choose a store —"}</option>
            {stores.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] font-display font-black uppercase tracking-widest">{lang === "es" ? "Puntos" : "Points"}</span>
          <input type="number" min={1} max={100} value={cfg.points_per_correct} onChange={(e) => setCfg({ ...cfg, points_per_correct: parseInt(e.target.value || "10", 10) })} data-testid="duel-cfg-points" className="brutal-sm mt-1 w-full py-2 px-2 bg-white font-display font-bold" />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-[10px] font-display font-black uppercase tracking-widest">{lang === "es" ? "Tiempo" : "Time"}</span>
            <input type="number" min={5} max={180} value={cfg.seconds_per_question} onChange={(e) => setCfg({ ...cfg, seconds_per_question: parseInt(e.target.value || "30", 10) })} data-testid="duel-cfg-time" className="brutal-sm mt-1 w-full py-2 px-2 bg-white font-display font-bold" />
          </label>
          <label className="block">
            <span className="text-[10px] font-display font-black uppercase tracking-widest">{lang === "es" ? "Errores" : "Strikes"}</span>
            <input type="number" min={1} max={5} value={cfg.max_strikes} onChange={(e) => setCfg({ ...cfg, max_strikes: parseInt(e.target.value || "3", 10) })} data-testid="duel-cfg-strikes" className="brutal-sm mt-1 w-full py-2 px-2 bg-white font-display font-bold" />
          </label>
        </div>
      </div>

      {/* Team assignment */}
      <div className="brutal bg-white p-4">
        <div className="grid grid-cols-2 gap-3 mb-3">
          <label className="block">
            <span className="text-[10px] font-display font-black uppercase tracking-widest" style={{ color: TEAM_COLOR.A }}>{lang === "es" ? "Nombre Equipo A" : "Team A name"} ({counts.a}/3)</span>
            <input value={teamA} onChange={(e) => setTeamA(e.target.value)} data-testid="duel-name-a" className="brutal-sm mt-1 w-full py-2 px-2 bg-white font-display font-bold" />
          </label>
          <label className="block">
            <span className="text-[10px] font-display font-black uppercase tracking-widest" style={{ color: TEAM_COLOR.B }}>{lang === "es" ? "Nombre Equipo B" : "Team B name"} ({counts.b}/3)</span>
            <input value={teamB} onChange={(e) => setTeamB(e.target.value)} data-testid="duel-name-b" className="brutal-sm mt-1 w-full py-2 px-2 bg-white font-display font-bold" />
          </label>
        </div>
        <div className="text-[10px] font-display font-black uppercase tracking-widest text-neutral-500 mb-2 flex items-center gap-1"><Users className="w-3 h-3" /> {lang === "es" ? "Vendedores de la tienda — asigna 3 y 3" : "Store sellers — assign 3 and 3"}</div>
        {roster.length === 0 ? (
          <div className="text-sm italic text-neutral-500 py-4 text-center">{store ? (lang === "es" ? "No hay vendedores en esta tienda." : "No sellers in this store.") : (lang === "es" ? "Elige una tienda para ver sus vendedores (o agrega jugadores manualmente abajo)." : "Choose a store first (or add players manually below).")}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto">
            {roster.map((r) => {
              const team = assign[r.seller_id];
              return (
                <div key={r.seller_id} data-testid={`duel-roster-${r.seller_id}`} className={`brutal-sm p-2 flex items-center justify-between gap-2 ${team ? "" : "opacity-90"}`} style={{ background: team === "A" ? "#DBEAFE" : team === "B" ? "#FEE2E2" : "#fff" }}>
                  <span className="font-display font-bold text-sm truncate">{r.name}</span>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => setTeam(r.seller_id, "A")} data-testid={`duel-assign-a-${r.seller_id}`} className={`w-7 h-7 border-2 border-black rounded font-display font-black text-xs ${team === "A" ? "text-white" : "bg-white"}`} style={team === "A" ? { background: TEAM_COLOR.A } : undefined}>A</button>
                    <button onClick={() => setTeam(r.seller_id, "B")} data-testid={`duel-assign-b-${r.seller_id}`} className={`w-7 h-7 border-2 border-black rounded font-display font-black text-xs ${team === "B" ? "text-white" : "bg-white"}`} style={team === "B" ? { background: TEAM_COLOR.B } : undefined}>B</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Manual players */}
        <div className="mt-3 border-t-2 border-dashed border-neutral-300 pt-3" data-testid="duel-manual-section">
          <div className="text-[10px] font-display font-black uppercase tracking-widest text-neutral-500 mb-2 flex items-center gap-1"><UserPlus className="w-3 h-3" /> {lang === "es" ? "Agregar jugador manualmente" : "Add player manually"}</div>
          <div className="flex flex-wrap gap-2 items-center">
            <input
              value={manualName}
              onChange={(e) => setManualName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
              placeholder={lang === "es" ? "Nombre del jugador" : "Player name"}
              data-testid="duel-manual-name"
              className="brutal-sm flex-1 min-w-[160px] py-2 px-2 bg-white font-display font-bold"
            />
            <button type="button" onClick={() => addManual("A")} data-testid="duel-manual-add-a" className="brutal-sm px-3 py-2 text-white font-display font-black text-sm" style={{ background: TEAM_COLOR.A }}>+ A</button>
            <button type="button" onClick={() => addManual("B")} data-testid="duel-manual-add-b" className="brutal-sm px-3 py-2 text-white font-display font-black text-sm" style={{ background: TEAM_COLOR.B }}>+ B</button>
          </div>
          {manual.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2" data-testid="duel-manual-list">
              {manual.map((m) => (
                <span key={m.id} data-testid={`duel-manual-chip-${m.id}`} className="brutal-sm inline-flex items-center gap-1 px-2 py-1 text-white text-sm font-display font-bold" style={{ background: TEAM_COLOR[m.team] }}>
                  {m.name}
                  <button type="button" onClick={() => removeManual(m.id)} data-testid={`duel-manual-remove-${m.id}`} className="ml-1 hover:opacity-70"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Questions */}
      <div className="brutal bg-white p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] font-display font-black uppercase tracking-widest text-neutral-500">{lang === "es" ? "Preguntas del banco" : "Question bank"} · {selQ.length} {lang === "es" ? "elegidas" : "selected"}</div>
          <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="duel-qcat" className="brutal-sm py-1 px-2 bg-white font-display font-bold text-sm">
            <option value="">{lang === "es" ? "Todas las categorías" : "All categories"}</option>
            {cats.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-1 gap-1 max-h-72 overflow-y-auto">
          {shownQ.map((q) => (
            <label key={q.question_id} data-testid={`duel-q-${q.question_id}`} className="flex items-start gap-2 p-2 brutal-sm bg-white cursor-pointer">
              <input type="checkbox" checked={selQ.includes(q.question_id)} onChange={() => toggleQ(q.question_id)} className="mt-1" />
              <span className="text-sm"><span className="text-[10px] font-display font-black uppercase tracking-widest text-canales-blue mr-1">{q.category}/{q.topic}</span>{lang === "en" && q.prompt_en ? q.prompt_en : q.prompt_es}</span>
            </label>
          ))}
        </div>
      </div>

      <button onClick={create} disabled={busy} data-testid="duel-create-btn" className="w-full brutal-btn bg-canales-red text-white px-6 py-4 font-display font-black text-lg inline-flex items-center justify-center gap-2 disabled:opacity-60">
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Swords className="w-5 h-5" />} {lang === "es" ? "Crear duelo" : "Create duel"}
      </button>
    </div>
  );
}

/* ----------------- History + export ----------------- */
function HistoryTab({ lang, onRematched }) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = async () => { setLoading(true); try { const { data } = await api.get("/team-duel/matches"); setMatches(data.matches || []); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const open = async (m) => { try { const { data } = await api.get(`/team-duel/matches/${m.match_id}`); setDetail(data); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };

  const exportExcel = (m) => {
    const rows = (m.results || []).map((r) => ({ Equipo: m[r.team === "A" ? "team_a" : "team_b"]?.name || r.team, Vendedor: r.name, Puntos: r.points, Errores: r.strikes, Eliminado: r.eliminated ? "Sí" : "No", "Temas a reforzar": (r.wrong_topics || []).join(", ") }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, "Duelo");
    XLSX.writeFile(wb, `duelo_${m.code || m.match_id}.xlsx`);
  };
  const exportPDF = (m) => {
    const doc = new jsPDF();
    doc.setFontSize(16); doc.text("Duelo de Equipos — Canales Furniture", 14, 18);
    doc.setFontSize(11);
    doc.text(`Tienda: ${m.store || "-"}`, 14, 28);
    doc.text(`${m.team_a?.name}: ${m.team_scores?.A ?? 0}   vs   ${m.team_b?.name}: ${m.team_scores?.B ?? 0}`, 14, 36);
    const win = m.winner_team ? (m[m.winner_team === "A" ? "team_a" : "team_b"]?.name) : "Empate";
    doc.text(`Ganador: ${win}`, 14, 44);
    let y = 56; doc.setFontSize(10);
    doc.text("Vendedor | Equipo | Puntos | Errores | Eliminado | Temas a reforzar", 14, y); y += 6;
    (m.results || []).forEach((r) => {
      const tn = m[r.team === "A" ? "team_a" : "team_b"]?.name || r.team;
      const line = `${r.name} | ${tn} | ${r.points} | ${r.strikes} | ${r.eliminated ? "Sí" : "No"} | ${(r.wrong_topics || []).join(", ")}`;
      doc.text(line.substring(0, 95), 14, y); y += 6; if (y > 280) { doc.addPage(); y = 20; }
    });
    doc.save(`duelo_${m.code || m.match_id}.pdf`);
  };
  const rematch = async (m) => { try { const { data } = await api.post(`/team-duel/matches/${m.match_id}/rematch`, {}); if (data.ok) { toast.success(lang === "es" ? "Revancha lista" : "Rematch ready"); onRematched(data); } } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };

  if (loading) return <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;

  return (
    <div className="brutal bg-white overflow-hidden">
      {matches.length === 0 ? (
        <div className="p-10 text-center font-body text-neutral-500">{lang === "es" ? "Aún no hay duelos. Crea el primero en la pestaña Nuevo." : "No duels yet."}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-canales-cream border-b-2 border-black text-xs uppercase font-display font-black tracking-widest">
              <tr><th className="text-left px-4 py-3">{lang === "es" ? "Fecha" : "Date"}</th><th className="text-left px-4 py-3">{lang === "es" ? "Tienda" : "Store"}</th><th className="text-left px-4 py-3">{lang === "es" ? "Equipos" : "Teams"}</th><th className="text-left px-4 py-3">{lang === "es" ? "Estado" : "Status"}</th><th className="text-right px-4 py-3"></th></tr>
            </thead>
            <tbody>
              {matches.map((m) => (
                <tr key={m.match_id} data-testid={`duel-match-${m.match_id}`} className="border-b border-neutral-200">
                  <td className="px-4 py-3 text-xs text-neutral-600">{(m.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-3">{m.store || "—"}</td>
                  <td className="px-4 py-3 font-display font-bold">{m.team_a?.name} <span className="text-neutral-400">vs</span> {m.team_b?.name}</td>
                  <td className="px-4 py-3">
                    {m.status === "done"
                      ? <span className="inline-flex items-center gap-1 text-green-700 font-display font-bold"><Trophy className="w-3 h-3" /> {m.winner_team ? (m[m.winner_team === "A" ? "team_a" : "team_b"]?.name) : (lang === "es" ? "Empate" : "Tie")}</span>
                      : <span className="text-canales-blue font-display font-bold uppercase text-xs">{m.status}</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1 flex-wrap justify-end">
                      <button onClick={() => open(m)} className="brutal-sm bg-canales-blue text-white px-2 py-1 text-xs font-display font-bold" data-testid={`duel-view-${m.match_id}`}>{lang === "es" ? "Ver" : "View"}</button>
                      <button onClick={() => rematch(m)} className="brutal-sm bg-canales-yellow text-black px-2 py-1 text-xs font-display font-black inline-flex items-center gap-1" data-testid={`duel-rematch-${m.match_id}`}><Repeat className="w-3 h-3" /> {lang === "es" ? "Revancha" : "Rematch"}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setDetail(null)} data-testid="duel-detail-modal">
          <div className="brutal bg-white p-5 max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <div className="font-display font-black text-2xl">{detail.team_a?.name} <span className="text-neutral-400">vs</span> {detail.team_b?.name}</div>
              <div className="flex gap-1">
                <button onClick={() => exportPDF(detail)} className="brutal-sm bg-canales-red text-white px-2 py-1 text-xs font-display font-bold inline-flex items-center gap-1" data-testid="duel-export-pdf"><FileDown className="w-3 h-3" /> PDF</button>
                <button onClick={() => exportExcel(detail)} className="brutal-sm bg-green-700 text-white px-2 py-1 text-xs font-display font-bold inline-flex items-center gap-1" data-testid="duel-export-xlsx"><FileSpreadsheet className="w-3 h-3" /> Excel</button>
              </div>
            </div>
            <div className="text-sm text-neutral-600 mb-3">{detail.store} · {detail.team_scores?.A ?? 0} — {detail.team_scores?.B ?? 0} · {lang === "es" ? "Ganador" : "Winner"}: <b>{detail.winner_team ? (detail[detail.winner_team === "A" ? "team_a" : "team_b"]?.name) : (lang === "es" ? "Empate" : "Tie")}</b></div>
            <table className="w-full text-sm">
              <thead className="bg-canales-cream text-xs uppercase font-display font-black tracking-widest"><tr><th className="text-left px-2 py-2">{lang === "es" ? "Vendedor" : "Seller"}</th><th className="px-2 py-2">Eq</th><th className="px-2 py-2">Pts</th><th className="px-2 py-2">X</th><th className="text-left px-2 py-2">{lang === "es" ? "Temas a reforzar" : "Topics to review"}</th></tr></thead>
              <tbody>
                {(detail.results || []).map((r) => (
                  <tr key={r.seller_id} className="border-b border-neutral-200">
                    <td className="px-2 py-2 font-display font-bold">{r.name}{r.eliminated && <span className="ml-1 text-[9px] bg-neutral-700 text-white px-1 rounded uppercase">{lang === "es" ? "fuera" : "out"}</span>}</td>
                    <td className="px-2 py-2 text-center" style={{ color: TEAM_COLOR[r.team] }}>{r.team}</td>
                    <td className="px-2 py-2 text-center font-display font-black">{r.points}</td>
                    <td className="px-2 py-2 text-center text-canales-red font-display font-black">{r.strikes}</td>
                    <td className="px-2 py-2 text-xs text-neutral-600">{(r.wrong_topics || []).join(", ") || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function TeamDuelAdmin() {
  const { lang } = useLang();
  useAuth();
  const [tab, setTab] = useState("setup");
  const [created, setCreated] = useState(null);
  const [editInitial, setEditInitial] = useState(null);

  const goLive = (data) => { setCreated(data); setTab("live"); };
  const editFromLive = (matchDetail) => { setEditInitial(matchDetail); setTab("setup"); };

  return (
    <div className="space-y-5" data-testid="team-duel-admin">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h1 className="font-display font-black text-3xl sm:text-4xl inline-flex items-center gap-2"><Swords className="w-8 h-8 text-canales-red" /> {lang === "es" ? "Duelo de Equipos" : "Team Duel"}</h1>
          <p className="text-sm text-neutral-600 font-body mt-1">{lang === "es" ? "6 vendedores · 2 equipos · eliminación por 3 errores · en vivo por WebSocket." : "6 sellers · 2 teams · 3-strike elimination · live over WebSocket."}</p>
        </div>
        <div className="flex gap-1">
          <button onClick={() => { setEditInitial(null); setTab("setup"); }} data-testid="duel-tab-setup" className={`brutal-btn px-4 py-2 text-sm font-display font-black inline-flex items-center gap-1 ${tab === "setup" ? "bg-canales-blue text-white" : "bg-white"}`}><Plus className="w-4 h-4" /> {lang === "es" ? "Nuevo" : "New"}</button>
          <button onClick={() => setTab("live")} disabled={!created} data-testid="duel-tab-live" className={`brutal-btn px-4 py-2 text-sm font-display font-black inline-flex items-center gap-1 disabled:opacity-40 ${tab === "live" ? "bg-canales-red text-white" : "bg-white"}`}><Tv className="w-4 h-4" /> {lang === "es" ? "En vivo" : "Live"}</button>
          <button onClick={() => setTab("history")} data-testid="duel-tab-history" className={`brutal-btn px-4 py-2 text-sm font-display font-black inline-flex items-center gap-1 ${tab === "history" ? "bg-canales-blue text-white" : "bg-white"}`}><History className="w-4 h-4" /> {lang === "es" ? "Historial" : "History"}</button>
        </div>
      </div>

      {tab === "setup" && <SetupTab key={editInitial?.match_id || "new"} lang={lang} onCreated={goLive} initial={editInitial} />}
      {tab === "live" && (created ? <HostPanel key={created.code} created={created} lang={lang} onReplace={goLive} onEdit={editFromLive} /> : <div className="brutal bg-white p-8 text-center text-neutral-500">{lang === "es" ? "Crea un duelo primero." : "Create a duel first."}</div>)}
      {tab === "history" && <HistoryTab lang={lang} onRematched={goLive} />}
    </div>
  );
}
